import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, IconButton, CircularProgress } from '@mui/material';
import { CheckCircle, Clear } from '@mui/icons-material';
import DiscountList from './DiscountList';
import DiscountDetailPanel from './DiscountDetailPanel';

const scrollbar = {
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
};

const BillDiscountTab = ({
  discounts,
  orderSubtotal,
  billDiscountId,
  billDiscountName,
  billDiscountAmount,
  onSelectionChange,
  onRemove,
  isRemoving,
}) => {
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [customValue, setCustomValue]           = useState('');
  const [reason, setReason]                     = useState('');

  // Notify parent whenever selection changes
  useEffect(() => {
    onSelectionChange({ discount: selectedDiscount, customValue, reason });
  }, [selectedDiscount, customValue, reason, onSelectionChange]);

  const handleSelect = (d) => {
    // Toggle — deselect if same discount clicked again
    setSelectedDiscount((prev) => (prev?.id === d.id ? null : d));
    setCustomValue('');
    setReason('');
  };

  return (
    <Box sx={{ ...scrollbar, overflowY: 'auto', flexGrow: 1 }}>

      {/* Currently applied bill discount */}
      {billDiscountId && (
        <Box sx={{ px: 2, pt: 2, pb: 0 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem', mb: 0.75 }}>
            CURRENTLY APPLIED
          </Typography>
          <Box sx={{
            bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px',
            px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircle sx={{ fontSize: 16, color: '#16A34A', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: { xs: '0.85rem', md: '0.82rem' }, fontWeight: 700, color: '#166534' }}>
                  {billDiscountName}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#16A34A' }}>
                  −₹{parseFloat(billDiscountAmount || 0).toFixed(2)} off bill
                </Typography>
              </Box>
            </Stack>
            <IconButton
              size="small"
              onClick={onRemove}
              disabled={isRemoving}
              sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' }, flexShrink: 0 }}
            >
              {isRemoving ? <CircularProgress size={14} color="error" /> : <Clear fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Available discounts label */}
      <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem' }}>
          AVAILABLE DISCOUNTS
        </Typography>
      </Box>

      <DiscountList
        discounts={discounts}
        selectedId={selectedDiscount?.id}
        onSelect={handleSelect}
        emptyText="No bill discounts available for this store"
      />

      {selectedDiscount && (
        <DiscountDetailPanel
          discount={selectedDiscount}
          orderSubtotal={orderSubtotal}
          customValue={customValue}
          onCustomValueChange={setCustomValue}
          reason={reason}
          onReasonChange={setReason}
        />
      )}
    </Box>
  );
};

export default BillDiscountTab;
